/**
 * Patient document summary shell with a responsive, docked notes panel.
 *
 * Document content and collaboration notes remain separate domains: opening a
 * note never mutates the notification document unread badge.
 */

import {
  ArrowLeft,
  ChevronDown,
  ExternalLink,
  FileText,
  NotebookPen,
} from "lucide-react";
import { useState } from "react";
import { Button } from "risa-oasis-ui_v2";
import type { DocumentType, Patient } from "../../types";
import { DiagnosisSection } from "./components/DiagnosisSection";
import { DocumentSummaryContent } from "./components/DocumentSummaryContent";
import { PatientDetailsGrid } from "./components/PatientDetailsGrid";
import { PatientIdentityBanner } from "./components/PatientIdentityBanner";
import { PatientNotesPanel } from "./components/PatientNotesPanel";
import "./DocumentSummaryView.scss";

interface DocumentSummaryViewProps {
  patient: Patient | null;
  documentType: DocumentType;
  isLoading?: boolean;
  notesOpen: boolean;
  targetNoteId?: string;
  sourceUnavailable?: boolean;
  onNotesOpenChange: (open: boolean) => void;
  onBack: () => void;
  /** Destination label for the empty-state return action. */
  backLabel?: string;
  onPatientUpdate?: (patient: Patient) => void;
}

const ONCO_EMR_BASE = "https://secure2.oncoemr.com";
const ONCO_EMR_LOCATION = "LH_06A43HYPY47HHDGK2M6N";
const ONCO_EMR_ROUTES = [
  { label: "View Documents", path: "nav/documents" },
  { label: "View Summary", path: "nav/summary" },
  { label: "View Treatment Plan", path: "nav/treatment-plan" },
];

/**
 * Render the selected report and optionally dock the patient-wide notes panel.
 *
 * @param props - Patient summary, deep-link state, and navigation callbacks
 * @returns Document summary with patient collaboration notes
 */
export function DocumentSummaryView({
  patient,
  documentType,
  isLoading,
  notesOpen,
  targetNoteId,
  sourceUnavailable,
  onNotesOpenChange,
  onBack,
  backLabel = "Back to Patient Notifications",
}: DocumentSummaryViewProps) {
  const [oncoEmrOpen, setOncoEmrOpen] = useState(false);
  if (!patient) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-uiGray-50">
        <div className="text-center">
          <p className="mb-4 text-lg font-medium">No patient selected</p>
          <Button buttonType="primary" size="medium" disabled={false} onClick={onBack}>
            {backLabel}
          </Button>
        </div>
      </div>
    );
  }

  /**
   * Open the selected OncoEMR patient destination in a separate tab.
   *
   * @param path - Relative OncoEMR record route
   * @returns Nothing
   */
  const openOncoEmr = (path: string) => {
    setOncoEmrOpen(false);
    const identifier = patient.patient_fhir_identifier;
    if (!identifier) {
      window.alert(`No FHIR identifier available for patient ${patient.mrn}`);
      return;
    }
    const patientId = identifier.split(".")[0].replaceAll("--", "_");
    window.open(
      `${ONCO_EMR_BASE}/${path}?PID=${patientId}&locationId=${ONCO_EMR_LOCATION}`,
      "_blank",
    );
  };

  /** Open the exact signed source URL currently attached to the patient row. */
  const openSource = () => {
    const sourcePath = patient.sourceDocuments?.[documentType];
    if (sourcePath) window.open(sourcePath, "_blank");
    else window.alert(`Source document not available for ${documentType}`);
  };

  return (
    <div className="flex h-full min-h-screen bg-uiGray-50">
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="container-app py-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Button buttonType="secondary" size="medium" disabled={false} onClick={onBack}>
                <span className="inline-flex items-center gap-2"><ArrowLeft size={16} /> Back</span>
              </Button>
              <h1 className="text-2xl font-bold">{documentType} - Clinical Summary</h1>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button buttonType="tertiary" size="medium" disabled={false} onClick={() => onNotesOpenChange(!notesOpen)}>
                <span className="inline-flex items-center gap-2"><NotebookPen size={16} /> Patient Notes</span>
              </Button>
              <div className="relative">
                <Button buttonType="tertiary" size="medium" disabled={false} onClick={() => setOncoEmrOpen((open) => !open)}>
                  <span className="inline-flex items-center gap-2"><ExternalLink size={16} /> View in OncoEMR <ChevronDown size={14} /></span>
                </Button>
                {oncoEmrOpen && (
                  <>
                    <button aria-label="Close OncoEMR menu" className="fixed inset-0 z-40 cursor-default" onClick={() => setOncoEmrOpen(false)} />
                    <div className="absolute right-0 z-50 mt-1 min-w-[210px] overflow-hidden rounded-md border bg-white shadow-lg">
                      {ONCO_EMR_ROUTES.map((route) => (
                        <button key={route.path} type="button" onClick={() => openOncoEmr(route.path)} className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-uiGray-50">
                          <ExternalLink size={14} /> {route.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <Button buttonType="tertiary" size="medium" disabled={false} onClick={openSource}>
                <span className="inline-flex items-center gap-2"><FileText size={16} /> View Source</span>
              </Button>
            </div>
          </div>
          {sourceUnavailable && (
            <div role="status" className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Source document unavailable. The patient-wide note remains available.
            </div>
          )}
          <div className="sticky top-0 z-10 bg-uiGray-50">
            <div className="compact-patient-header !mb-0 rounded-b-none border-b-0">
              <div className="patient-id-banner rounded-b-none">
                <PatientIdentityBanner patient={patient} />
              </div>
            </div>
          </div>
          <PatientDetailsGrid patient={patient} />
          {/* Disease Status hidden for now — set showDiseaseStatus back to
              `documentType === "Radiology"` to restore it. */}
          <DiagnosisSection patient={patient} showDiseaseStatus={false} />
          <DocumentSummaryContent patient={patient} documentType={documentType} isLoading={isLoading} />
        </div>
      </div>
      {notesOpen && (
        <PatientNotesPanel
          mrn={patient.mrn}
          patientName={patient.name}
          sourceDocumentId={patient.documentId}
          targetNoteId={targetNoteId}
          onClose={() => onNotesOpenChange(false)}
        />
      )}
    </div>
  );
}
